import { ComponentType, LazyExoticComponent, lazy } from 'react';

/**
 * Marks that this tab has already reloaded itself to recover a missing chunk, so a chunk that is
 * genuinely gone can never put the app in a reload loop.
 */
const RELOAD_MARKER = 'ot.chunkReloadAt';

/** How long a recovery reload suppresses the next one. Long enough to break a loop, short enough
 *  that a second deploy later in the same session still recovers on its own. */
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * True for the "the JS file this route needs is no longer on the server" family of errors, whatever
 * the bundler calls it: webpack raises `ChunkLoadError`, native ESM raises a TypeError about a
 * dynamically imported module.
 */
const isChunkLoadError = (error: unknown): boolean => {
	const name = (error as { name?: string } | null)?.name ?? '';
	const message = (error as { message?: string } | null)?.message ?? '';
	return (
		name === 'ChunkLoadError' ||
		/loading chunk .* failed/i.test(message) ||
		/failed to fetch dynamically imported module/i.test(message) ||
		/importing a module script failed/i.test(message)
	);
};

/** sessionStorage throws in some privacy modes; a recovery path must never fail on its own. */
const readMarker = (): number => {
	try {
		return Number(window.sessionStorage.getItem(RELOAD_MARKER) ?? 0);
	} catch {
		return 0;
	}
};

const writeMarker = (): void => {
	try {
		window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
	} catch {
		/* nothing to do: we just lose the loop guard, and the browser stops us anyway */
	}
};

/**
 * `React.lazy` that survives a chunk that is no longer on the server.
 *
 * Every route in this app is code-split — `contentRoutes.tsx` alone has more than two hundred lazy
 * imports — so *changing page* is what loads a chunk. When the bundle a tab is running no longer
 * matches what the server serves, the next navigation throws `ChunkLoadError` and the screen dies;
 * the user's own fix is to press refresh. That happens in development on every recompile, and in
 * production to anyone who had a tab open when we deployed.
 *
 * So the app does the refresh itself, once. The returned promise is left unsettled on purpose:
 * Suspense keeps showing the fallback while the reload happens, instead of flashing an error the
 * user is not meant to read. A second failure inside the cooldown rethrows, so a chunk that really
 * is gone surfaces as an error rather than looping.
 */
export default function lazyWithRetry<
	// Mirrors React's own `lazy` signature exactly, so the 200+ call sites keep type-checking
	// against their real props. Narrowing this breaks every component that takes props.
	T extends ComponentType<any>,
>(factory: () => Promise<{ default: T }>): LazyExoticComponent<T> {
	return lazy(async () => {
		try {
			return await factory();
		} catch (error) {
			if (!isChunkLoadError(error)) throw error;

			if (Date.now() - readMarker() < RELOAD_COOLDOWN_MS) {
				// We already tried this. Surface it instead of reloading forever.
				throw error;
			}

			writeMarker();
			window.location.reload();

			// Never settles: the page is going away, and Suspense holds the fallback until it does.
			return new Promise<{ default: T }>(() => {});
		}
	});
}
